import { useState, useMemo, useEffect } from "react";
import { Influencer } from "interfaces/influencer";
import { formatNumberKMBT, ImgWithErrorHandling } from "./InfluencerCard";
import { ReactComponent as InstagramIcon } from "assets/images/instagram-icon.svg";
import { ReactComponent as TikTokIcon } from "assets/images/tiktok-icon.svg";
import { Avatar } from "antd";
import "./CreatorCard.scss";
import { useNavigate } from "react-router-dom";
import { favoriteCreator, unfavoriteCreator } from "services/SauceService";
import { useDispatch, useSelector } from "react-redux";
import { BusinessState, getFavoritedCreators } from "redux-context/business";

type Creator = {
  foodieId: string;
  instagramFollowersCount: number;
  tiktokFollowersCount: number;
  top3PostsCoverImgUrl: string[];
  avatarUrl: string;
  username: string;
  name: string;
  categoriesIA: string[];
  tabStatus: Influencer["tabStatus"];
  inviteAvailable: boolean;
};

type CreatorCardProps = {
  creator: Creator;
  onInfluencerClick: (creator?: Creator) => void;
  index?: number;
};

export const AnimateHeartIcon = ({
  filled = false,
  ...props
}: {
  filled?: boolean;
  style?: React.CSSProperties;
}) => (
  <svg
    viewBox="0 0 24 24"
    width={28}
    height={28}
    fill={filled ? "#ff4d4f" : "#ccc"}
    stroke="none"
    xmlns="http://www.w3.org/2000/svg"
    {...props}
  >
    <path d="M12.1 8.64l-.1.1-.11-.11C9.14 5.92 5.1 7.24 5.1 10.28c0 2.54 2.4 4.36 6.55 8.05.23.2.57.2.8 0 4.15-3.69 6.55-5.51 6.55-8.05 0-3.04-4.04-4.36-6.45-1.64z" />
  </svg>
);

const PALETTE = [
  "#F6A623", // brand-primary (orange)
  "#E04A00", // brand-secondary
  "#FF5C00", // brand-cta
  "#52c41a", // success (green)
  "#faad14", // warning (yellow)
  "#ff4d4f", // error (red)
  "#1677ff", // info (blue)
  "#1f1f1f", // gray-900
  "#434343", // gray-700
  "#8c8c8c", // gray-500
  "#d1a1a1ff", // gray-300
];

const hexToRgba = (hex: string, alpha = 1) => {
  const h = hex.replace("#", "");
  const bigint = parseInt(
    h.length === 3
      ? h
          .split("")
          .map((c) => c + c)
          .join("")
      : h,
    16
  );
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const CreatorCard: React.FC<CreatorCardProps> = (props) => {
  const { creator, onInfluencerClick, index = 0 } = props;
  const navigate = useNavigate();
  const animationDelay = `${Math.min(index * 60, 600)}ms`;
  const dispatch = useDispatch<any>();

  const { business, favoritedCreators } = useSelector(
    (state: BusinessState) => state.business
  );
  const [liked, setLiked] = useState<boolean>(false);
  const [heartAnimating, setHeartAnimating] = useState<boolean>(false);

  const handleHeartClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setHeartAnimating(true);
    const params = {
      businessId: business._id,
      creatorId: creator.foodieId,
    };
    try {
      if (liked) {
        await unfavoriteCreator(params);
      } else {
        await favoriteCreator(params);
      }
      dispatch(getFavoritedCreators());
    } catch (error) {
      console.error("Error favoriting creator:", error);
    }
    setLiked((prev) => !prev);
    setHeartAnimating(false);
  };

  const [platform, setPlatform] = useState<"ig" | "tt">(() => {
    const ig = creator.instagramFollowersCount ?? 0;
    const tt = creator.tiktokFollowersCount ?? 0;
    // prefer the platform with more followers, fallback to IG
    return ig >= tt ? "ig" : "tt";
  });

  const topImages = creator.top3PostsCoverImgUrl?.slice(0, 3) ?? [];
  while (topImages.length < 3) {
    topImages.push(`https://via.placeholder.com/400x300?text=Post`);
  }

  const stats = useMemo(() => {
    const igStats = (creator as any).instagramStats;
    const ttStats = (creator as any).tiktokStats;

    const followers =
      platform === "ig"
        ? creator?.instagramFollowersCount ?? 0
        : creator?.tiktokFollowersCount ?? 0;

    const engagement =
      platform === "ig"
        ? igStats?.engagementRate ?? 0
        : ttStats?.engagementRate ?? 0;

    const avgLikesFromField =
      platform === "ig" ? igStats?.avgLikes : ttStats?.avgLikes;

    const avgLikes =
      typeof avgLikesFromField === "number"
        ? Math.round(avgLikesFromField)
        : Math.round(followers * (engagement / 100));

    return { followers, engagement, avgLikes };
  }, [platform, creator]);

  const invited = creator.tabStatus === "accepted";

  const handleClick = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    navigate(`/creators/${creator.foodieId}/invite`);
  };

  // deterministic mapping: stable index per tag string
  const colorIndexForTag = (tag: string) => {
    let hash = 0;
    for (let i = 0; i < tag.length; i++) {
      // simple 32-bit hash
      hash = (hash << 5) - hash + tag.charCodeAt(i);
      hash |= 0;
    }
    return Math.abs(hash) % PALETTE.length;
  };

  const colorStyleForTag = (tag: string) => {
    const color = PALETTE[colorIndexForTag(tag)];
    return {
      backgroundColor: hexToRgba(color, 0.12),
      color: color,
    } as React.CSSProperties;
  };

  useEffect(() => {
    const isLiked = favoritedCreators?.some(
      (fav) => fav.creatorId === creator.foodieId
    );
    setLiked(isLiked);
  }, [favoritedCreators, creator.foodieId]);

  return (
    <div
      className="creator-card"
      onClick={() => onInfluencerClick(creator)}
      role="button"
      aria-label={`Open creator ${creator.username ?? "profile"}`}
      style={{ "--cc-animation-delay": animationDelay } as React.CSSProperties}
    >
      <div className="creator-card__content">
      <div className="creator-card__top">
        <div className="creator-card__images">
          {(creator.top3PostsCoverImgUrl?.length || 0) > 0 ? (
            creator.top3PostsCoverImgUrl?.map((src, idx) => (
              <div
                key={idx}
                className={`creator-card__image creator-card__image--${idx}`}
              >
                <ImgWithErrorHandling src={src} alt={`post-${idx}`} />
              </div>
            ))
          ) : (
            <div>
              <p
                style={{
                  padding: 20,
                  color: "#999999",
                  textAlign: "center",
                }}
              >
                Influencer profile is being enriched
              </p>
            </div>
          )}
        </div>
      </div>

      <div className="creator-card__body">
        <button
          className={`creator-card__heart${liked ? " is-liked" : ""}${
            heartAnimating ? " is-animating" : ""
          }`}
          onClick={handleHeartClick}
          aria-label={liked ? "Desfavoritar" : "Favoritar"}
        >
          <AnimateHeartIcon filled={liked} />
        </button>
        <div className="creator-card__header">
          <Avatar
            src={creator.avatarUrl}
            className="creator-card__avatar"
            alt={creator.username ?? "avatar"}
          />

          <div className="creator-card__info">
            <div className="creator-card__username">
              <span
                className="creator-card__username-text"
                title={creator.name ?? "Creator"}
              >
                {creator.name ?? "Creator"}
              </span>
            </div>
            <div
              className="creator-card__tags"
              onScroll={(e) => e.stopPropagation()}
            >
              {creator?.categoriesIA?.slice(0, 6)?.map((t) => (
                <span
                  key={t}
                  className="creator-card__tag"
                  style={colorStyleForTag(t)}
                >
                  #{t}
                </span>
              ))}
            </div>
          </div>
        </div>

        <div
          className="creator-card__social"
          onClick={(e) => e.stopPropagation()}
          role="tablist"
          aria-label="Platform selector"
        >
          <div className="creator-card__buttons">
            {(creator.instagramFollowersCount || 0) > 0 && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setPlatform("ig");
                }}
                aria-pressed={platform === "ig"}
                aria-label="Instagram"
                className={`creator-card__social-btn creator-card__social-btn--ig ${
                  platform === "ig" ? "is-active" : ""
                }`}
              >
                <InstagramIcon fill="#db2777" />
                <span className="creator-card__social-text">IG</span>
              </button>
            )}
            {(creator.tiktokFollowersCount || 0) > 0 && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setPlatform("tt");
                }}
                aria-pressed={platform === "tt"}
                aria-label="TikTok"
                className={`creator-card__social-btn creator-card__social-btn--tt ${
                  platform === "tt" ? "is-active" : ""
                }`}
              >
                <TikTokIcon stroke="#000" fill="#000" />
                <span className="creator-card__social-text">TT</span>
              </button>
            )}
          </div>
        </div>

        {stats.followers > 0 && (
          <div className="creator-card__stats">
            <div className="creator-card__stat">
              <div className="creator-card__stat-value">
                {formatNumberKMBT(stats.followers)}
              </div>
              <div className="creator-card__stat-label">Followers</div>
            </div>
            {stats.engagement > 0 && (
              <div className="creator-card__stat">
                <div className="creator-card__stat-value">
                  {stats.engagement ? `${stats.engagement}%` : "-"}
                </div>
                <div className="creator-card__stat-label">Engagement</div>
              </div>
            )}

            {stats.avgLikes > 0 && (
              <div className="creator-card__stat">
                <div className="creator-card__stat-value">
                  {formatNumberKMBT(stats.avgLikes)}
                </div>
                <div className="creator-card__stat-label">Avg. Likes</div>
              </div>
            )}
          </div>
        )}

        <button
          onClick={handleClick}
          className={`creator-card__action ${invited ? "is-invited" : ""}`}
          disabled={invited}
          hidden={!creator.inviteAvailable}
        >
          {invited ? "Invite Sent" : "Invite"}
        </button>
      </div>
      </div>
    </div>
  );
};

export default CreatorCard;
